//================================================================
//  Page: RequestDetailsM
//================================================================

//  Purpose: Detailed Request page - shows individual Request in detail (Mobile View)

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import BreadCrumbs from '../../../Components/Breadcrumbs/BreadCrumbs';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import ActivityFeed from '../../../Components/ActivityFeed/ActivityFeed';
import ManageRequest from '../../../Components/ManageRequest/ManageRequest';
import TabViewM from '../../../Components/TabViewM/TabViewM';
import Footer from '../../../Components/Footer/Footer.js';

// > Digital Request Forms 
import NewInitiative from '../../../Components/DigitalRequestForms/NewInitiative';
import ContractRenewal from '../../../Components/DigitalRequestForms/ContractRenewal';
import Enhancement from '../../../Components/DigitalRequestForms/Enhancement';
import AdditionalLicense from '../../../Components/DigitalRequestForms/AdditionalLicense';
import Decommission from '../../../Components/DigitalRequestForms/Decommission';

// > Commercial Request Forms
import EstablishNewAgreement from '../../../Components/CommercialRequestForms/EstablishNewAgreement.js';
import VaryExtendRenewExistingAgreement from '../../../Components/CommercialRequestForms/VaryExtendRenewExistingAgreement.js';
import TerminateExistingAgreement from '../../../Components/CommercialRequestForms/TerminateExistingAgreement.js';
import AssignNovateAgreement from '../../../Components/CommercialRequestForms/AssignNovateAgreement.js';
import EstablishNDA from '../../../Components/CommercialRequestForms/EstablishNDA.js';

//Functions
import DocumentListener from '../../../Library/DocumentListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images
import Expand from '../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../Components/Images/Icon_Expand_Teal.svg';

//CSS


export default function RequestDetailsM() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const params = useParams();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Contains Firestore Document of the request
    const [requestDoc, setRequestDoc] = useState(undefined);

    // Used to toggle view of details
    const [viewDetails, setViewDetails] = useState(true);

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Get the Request document
    useEffect(() => {

        const requestid = params.id;

        function onLoadChange(document) {

            if (!(document?.tasktype === 'Digital Request' || document?.tasktype === 'Digital Commercial Request')) return setPageStatus('forbidden');

            setRequestDoc(document);
            setPageStatus('onload');

        }

        function onError(error) {
            console.log(error);

            setPageStatus('error-fatal');
        }

        const unsubscribe = DocumentListener('requests', requestid, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        }

    }, [params,getUser])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            header={
                <div className='flex flex-col px-[18px] py-[21px] bg-neutral-100 h-full w-full border-b border-b-neutral-300 border-1'  hidden={pageStatus !== 'onload'}>
                    <BreadCrumbs rootLinkName={'My Requests'} rootLinkRoute={'/Requests'} childLinkName={requestDoc?.parentrequestid?.toUpperCase()}></BreadCrumbs>
                    <h4 className='flex items-center h-full cursor-default text-[22px] mb-0'>{requestDoc?.taskname}</h4>
                </div>}
            body={
                <div className='flex flex-col'>

                    {/* REQUEST INFORMATION */}
                    <div className='p-[18px]'>

                        <div className='flex flex-col gap-6'>

                            {/* Name */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Name</label>
                                <p className='m-0'>{requestDoc?.taskname}</p>
                            </div>

                            {/* Short Description */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Short Description</label>
                                <p className='m-0'>{requestDoc?.taskdescription}</p>
                            </div>

                            {/* Assigned To */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Assigned To</label>
                                <div className='flex flex-col gap-1'>
                                    <p className='m-0' hidden={requestDoc?.assignedto?.email === 'Unassigned'}>
                                        {requestDoc?.assignedto?.givenname} {requestDoc?.assignedto?.surname}
                                    </p>
                                    <p className={`m-0 text-[#424242] ${requestDoc?.assignedto?.email !== 'Unassigned' && 'text-sm'}`}>
                                        {requestDoc?.assignedto?.email}
                                    </p>
                                </div>
                            </div>

                            {/* Status */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Status</label>
                                <StatusLabel status={requestDoc?.status} tasktype={requestDoc?.tasktype}></StatusLabel>
                            </div>

                            {/* Created Date */}
                            <div className='flex flex-col gap-3'>
                                <label className='font-semibold'>Created Date</label>
                                <p className='m-0'>{ConvertDate(requestDoc?.createddate)}</p>
                            </div>

                        </div>

                    </div>

                    <hr className='text-neutral-500 p-0 m-0'></hr>

                    {/* VIEW MORE DETAILS */}
                    <div className='flex flex-row gap-3 px-[18px] py-2 items-center justify-between cursor-pointer' onClick={() => setViewDetails(!viewDetails)}>

                        {/* Title */}
                        <div className='flex flex-row items-center gap-2 py-2'>
                            <img className='cursor-pointer' src={viewDetails ? Expand : Collapse} alt={'>'}></img>
                            <h4 className='text-[16px] m-0'> {viewDetails ? 'View Less Details' : 'View More Details'} </h4>
                        </div>

                    </div>
                    <div hidden={!viewDetails}>
                        <TabViewM
                            defaultView={1}
                            tabs={[
                                {
                                    id: 1,
                                    title: 'Request Details',
                                    content: (() => {
                                        switch (requestDoc?.requesttype) {

                                            // Digital Request Forms
                                            case 'New Initiative or Technology': return <NewInitiative task={requestDoc}></NewInitiative>;
                                            case 'Contract Renewal': return <ContractRenewal task={requestDoc}></ContractRenewal>;
                                            case 'Enhancement': return <Enhancement task={requestDoc}></Enhancement>;
                                            case 'Additional Licenses or Overages': return <AdditionalLicense task={requestDoc}></AdditionalLicense>;
                                            case 'Decommission': return <Decommission task={requestDoc}></Decommission>;

                                            // Commercial Request Forms
                                            case 'Establish New Agreement': return <EstablishNewAgreement task={requestDoc}></EstablishNewAgreement>
                                            case 'Vary/Extend/Renew Existing Agreement': return <VaryExtendRenewExistingAgreement task={requestDoc}></VaryExtendRenewExistingAgreement>
                                            case 'Terminate Existing Agreement': return <TerminateExistingAgreement task={requestDoc}></TerminateExistingAgreement>;
                                            case 'Assign/Novate Agreement': return <AssignNovateAgreement task={requestDoc}></AssignNovateAgreement>;
                                            case 'Establish NDA': return <EstablishNDA task={requestDoc}></EstablishNDA>;


                                            default: return 'Form not found';
                                        }
                                    })(),
                                    disabled: false
                                },
                            ]}>

                        </TabViewM>
                    </div>

                    <hr className='m-0 text-neutral-500'></hr>

                    {/* TRACKING HISTORY */}
                    <TabViewM
                        defaultView={1}
                        tabs={[
                            {
                                id: 1,
                                title: 'Tracking History',
                                content:
                                    <div className='p-2'>
                                        <ActivityFeed
                                            task={requestDoc}
                                        ></ActivityFeed>
                                    </div>,
                                disabled: false,
                            }
                        ]}
                    ></TabViewM>

                    <div
                        hidden={requestDoc?.status !== 'In Progress'}
                    >
                        <Footer></Footer>
                    </div>

                    {/* MANAGE REQUEST - Only shows on pending requests */}
                    <ManageRequest requestDoc={requestDoc}></ManageRequest>

                </div>}
        ></PageComponent>
    )
}